import React, { useCallback, useRef, useState } from "react";

import { Recipe, recipesApi } from "@/client/api";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorMessage,
  Input,
  Link,
  PageHeader,
  TopLoadingBar,
} from "@/client/components/ui";
import { useDataVersion } from "@/client/context/DataVersionContext";
import { useApi } from "@/client/hooks/useApi";
import { useKey } from "@/client/hooks/useKey";
import { AddIcon, RecipeIcon } from "@/client/icons";
import { normalize } from "@/client/utils";

type FilterType = "all" | "main" | "side" | "base" | "dessert";

const FILTER_LABELS: Record<FilterType, string> = {
  all: "Todas",
  main: "Principales",
  side: "Guarniciones",
  base: "Bases",
  dessert: "Postres",
};

function RecipeCard({ recipe, index }: { recipe: Recipe; index?: number }) {
  const stockCount = recipe.available_servings ?? recipe.total_servings_remaining ?? 0;
  const hasStock = stockCount > 0;
  const ingredients = recipe.ingredients_preview?.split(",") ?? [];
  const componentNames = recipe.components_preview?.split(",") ?? [];

  return (
    <Link href={`/recetas/${recipe.id}`}>
      <Card className="p-4 flex flex-col gap-2.5 h-full" index={index} hoverable>
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-serif text-base text-term-text leading-tight">{recipe.name}</h3>
          {recipe.type && <Badge value={recipe.type} className="shrink-0 mt-0.5" />}
        </div>

        {(componentNames.length > 0 || ingredients.length > 0) && (
          <div className="flex flex-col gap-0.5">
            {componentNames.length > 0 && (
              <p className="text-sm text-term-text leading-relaxed line-clamp-1">
                {componentNames.join(", ")}
              </p>
            )}
            {ingredients.length > 0 && (
              <p className="text-sm text-term-text-muted leading-relaxed line-clamp-2">
                {ingredients.join(", ")}
              </p>
            )}
          </div>
        )}

        <div className="flex items-center gap-3 text-xs text-term-text-muted mt-auto pt-1">
          {recipe.servings != null && <span>{recipe.servings} porc.</span>}
          {(recipe.step_count ?? 0) > 0 && (
            <span>
              {recipe.step_count} paso{recipe.step_count !== 1 ? "s" : ""}
            </span>
          )}
          {hasStock && (
            <span className="flex items-center gap-1 text-term-user font-medium ml-auto">
              <span className="w-1.5 h-1.5 rounded-full bg-term-user inline-block" />
              {stockCount} en stock
            </span>
          )}
        </div>
      </Card>
    </Link>
  );
}

export function RecipesPage() {
  const { dataVersion } = useDataVersion();
  const [filter, setFilter] = useState<FilterType>("all");
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  useKey(
    "/",
    (e) => {
      e.preventDefault();
      searchRef.current?.focus();
    },
    { ignoreInputs: true },
  );

  const fetcher = useCallback(() => {
    void dataVersion;
    return recipesApi.list();
  }, [dataVersion]);
  const { data, loading, error, refetch } = useApi(fetcher, [dataVersion]);

  if (loading) return <TopLoadingBar visible />;
  if (error) return <ErrorMessage message={error} onRetry={refetch} />;
  if (!data) return null;

  const filtered = data.filter((r) => {
    if (filter !== "all" && r.type !== filter) return false;
    if (search && !normalize(r.name).includes(normalize(search))) return false;
    return true;
  });

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto flex flex-col gap-6">
      <PageHeader
        title="Recetas"
        icon={<RecipeIcon width={22} height={22} />}
        subtitle={`${data.length} receta${data.length !== 1 ? "s" : ""}`}
        actions={
          <Link href="/recetas/nueva">
            <Button variant="primary" size="sm">
              <AddIcon width={14} height={14} />
              Nueva receta
            </Button>
          </Link>
        }
      />

      {/* Search + filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          value={search}
          onChange={setSearch}
          type="search"
          placeholder="Buscar recetas..."
          className="sm:max-w-xs"
          autoFocus
          inputRef={searchRef}
        />
        <div className="flex items-center gap-1.5 flex-wrap">
          {(["all", "main", "side", "base", "dessert"] as FilterType[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer transition-all ${
                filter === f
                  ? "bg-term-accent text-white"
                  : "bg-term-raised border border-term-border text-term-text-dim hover:text-term-text"
              }`}
            >
              {FILTER_LABELS[f]}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <EmptyState
          icon={<RecipeIcon strokeWidth={1.25} />}
          title={data.length === 0 ? "Sin recetas" : "Sin resultados"}
          description={
            data.length === 0
              ? "Creá tu primera receta para empezar."
              : search
                ? `No se encontraron recetas para "${search}"`
                : "No hay recetas en esta categoría."
          }
          action={
            data.length === 0 ? (
              <Link href="/recetas/nueva">
                <Button variant="primary" size="sm">
                  <AddIcon width={14} height={14} />
                  Nueva receta
                </Button>
              </Link>
            ) : undefined
          }
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((r, i) => (
            <RecipeCard key={r.id} recipe={r} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
